import { DollarSign, ShoppingCart, Star, Users, XCircle, Ban, Building2, UserCheck, Eye, MousePointerClick, Target, Trophy, LogOut } from 'lucide-react';
import moment from 'moment';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from "react-redux";
import { Row } from 'reactstrap';
import { Colxx } from '../../../components/common/CustomBootstrap';
import { useDailyAnalytics } from '../../../components/dashboard/DailyAnalyticsFetcher';
import {
  useFeaturedSpDaily,
  aggregateFeaturedTotals,
  aggregateFeaturedByPlacement,
  hasFeaturedActivity,
  isFeaturedActive,
  listPlacements,
  formatPlacementLabel,
  PLACEMENT_COLORS,
} from '../../../components/dashboard/FeaturedSpDailyFetcher';
import DashboardCharts from '../../../components/dashboard/DashboardCharts';
import FeaturedCharts from '../../../components/dashboard/FeaturedCharts';
import DateRangeSelector from '../../../components/dashboard/DateRangeSelector';
import StatCard from '../../../components/dashboard/StatCard';
import { ORDER_COLLECTION } from '../../../config/env';
import RecentOrders from '../../../containers/dashboards/RecentOrders';
import TopProviders from '../../../containers/dashboards/topProviders';
import TopWashers from '../../../containers/dashboards/topWashers';
import { firestore } from '../../../helpers/Firebase';
import { accessTypes, formatNumber } from '../../../helpers/Utils';
import SelectedServiceProvider from './components/SelectedServiceProvider';

// Main dashboard component using hooks
const DefaultDashboard = () => {

  // Get data from Redux store with deep equality check
  const { user } = useSelector(state => state.authUser);
  const { provider, washers } = useSelector(state => state.providers);


  // State management
  const [bookings, setBookings] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Date range state
  const [dateRangeState, setDateRangeState] = useState(() => {
    const endDate = moment().subtract(1, 'day').endOf('day');
    const startDate = endDate.clone().subtract(6, 'days').startOf('day');
    return {
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      startTimestamp: startDate.valueOf(),
      endTimestamp: endDate.valueOf(),
    };
  });

  // Fetch analytics data
  const {
    data: dailyAnalytics,
    previousData: previousDailyAnalytics,
    loading: analyticsLoading,
    error: analyticsError
  } = useDailyAnalytics(dateRangeState);

  // Fetch featured SP performance (GA4-sourced, via featuredSpDaily collection).
  // Admin sees aggregate across all SPs; SP user sees their own row only.
  const {
    data: featuredDaily,
    previousData: previousFeaturedDaily,
    loading: featuredLoading,
    error: featuredError,
  } = useFeaturedSpDaily(dateRangeState);

  // Memoize user role check
  const isAdmin = useMemo(() => {
    return accessTypes.includes(user?.accessType);
  }, [user?.accessType]);

  // Decide whether to render the featured analytics (cards + charts).
  // Admin on the main dashboard always sees the aggregate. SP users only see
  // it if they are in an ACTIVE featured window (featured.endDate in the
  // future) OR they have any historical featured activity in the selected
  // date range — so SPs whose featuring has expired or was removed still
  // see their past numbers when the range overlaps.
  const showFeatured = useMemo(() => {
    if (isAdmin) return true;
    const myProvider = provider?.[user?.uid];
    if (isFeaturedActive(myProvider?.featured)) return true;
    return hasFeaturedActivity(featuredDaily, user?.uid);
  }, [isAdmin, provider, user?.uid, featuredDaily]);

  // Memoize date ranges for current and previous periods
  const { currentPeriodRange, previousPeriodRange } = useMemo(() => {
    if (!dateRangeState?.startDate || !dateRangeState?.endDate) {
      return { currentPeriodRange: null, previousPeriodRange: null };
    }

    const currentStart = moment(dateRangeState.startDate);
    const currentEnd = moment(dateRangeState.endDate);
    const periodLength = currentEnd.diff(currentStart, 'days');

    const previousStart = currentStart.clone().subtract(periodLength + 1, 'days');
    const previousEnd = currentStart.clone().subtract(1, 'days');

    return {
      currentPeriodRange: `${currentStart.format('MMM DD, YYYY')} - ${currentEnd.format('MMM DD, YYYY')}`,
      previousPeriodRange: `${previousStart.format('MMM DD, YYYY')} - ${previousEnd.format('MMM DD, YYYY')}`
    };
  }, [dateRangeState?.startDate, dateRangeState?.endDate]);

  // Calculate service providers and workers counts
  // Only count items with valid created timestamps for consistency with period calculations
  const providersStats = useMemo(() => {
    const allProviders = Object.values(provider || {});
    // Only count providers with valid created timestamp
    const providersWithCreated = allProviders.filter(p => p.created && typeof p.created === 'number');
    const totalProviders = providersWithCreated.length;
    const activeProviders = providersWithCreated.filter(p => p.active && !p.deActivated).length;
    return { total: totalProviders, active: activeProviders };
  }, [provider]);

  const workersStats = useMemo(() => {
    const allWorkers = Object.values(washers || {});
    // Only count workers with valid created timestamp
    const workersWithCreated = allWorkers.filter(w => w.created && typeof w.created === 'number');
    const totalWorkers = workersWithCreated.length;
    const activeWorkers = workersWithCreated.filter(w => w.active && !w.deActivated).length;
    return { total: totalWorkers, active: activeWorkers };
  }, [washers]);

  // Calculate providers and workers created in current and previous periods
  const providersPeriodStats = useMemo(() => {
    const allProviders = Object.values(provider || {});
    const currentStart = dateRangeState.startTimestamp;
    const currentEnd = dateRangeState.endTimestamp;

    // Calculate previous period timestamps
    const periodLength = currentEnd - currentStart;
    const previousStart = currentStart - periodLength - 1;
    const previousEnd = currentStart - 1;

    // Count providers created in current period
    const currentCreated = allProviders.filter(p => {
      const created = p.created;
      return created && typeof created === 'number' && created >= currentStart && created <= currentEnd;
    }).length;

    // Count providers created in previous period
    const previousCreated = allProviders.filter(p => {
      const created = p.created;
      return created && typeof created === 'number' && created >= previousStart && created <= previousEnd;
    }).length;

    // Calculate totals at the end of previous period (all that existed before current period)
    // Only count providers that have a created timestamp
    const previousPeriodTotal = allProviders.filter(p => {
      const created = p.created;
      // Only include providers with valid created timestamp that existed before current period
      return created && typeof created === 'number' && created <= previousEnd;
    }).length;

    const previousPeriodActive = allProviders.filter(p => {
      const created = p.created;
      // Only include providers with valid created timestamp that existed before current period and are currently active
      return created && typeof created === 'number' && created <= previousEnd && p.active && !p.deActivated;
    }).length;

    return {
      current: currentCreated,
      previous: previousCreated,
      previousPeriodTotal,
      previousPeriodActive
    };
  }, [provider, dateRangeState.startTimestamp, dateRangeState.endTimestamp]);

  const workersPeriodStats = useMemo(() => {
    const allWorkers = Object.values(washers || {});
    const currentStart = dateRangeState.startTimestamp;
    const currentEnd = dateRangeState.endTimestamp;

    // Calculate previous period timestamps
    const periodLength = currentEnd - currentStart;
    const previousStart = currentStart - periodLength - 1;
    const previousEnd = currentStart - 1;

    // Count workers created in current period
    const currentCreated = allWorkers.filter(w => {
      const created = w.created;
      return created && typeof created === 'number' && created >= currentStart && created <= currentEnd;
    }).length;

    // Count workers created in previous period
    const previousCreated = allWorkers.filter(w => {
      const created = w.created;
      return created && typeof created === 'number' && created >= previousStart && created <= previousEnd;
    }).length;

    // Calculate totals at the end of previous period (all that existed before current period)
    // Only count workers that have a created timestamp
    const previousPeriodTotal = allWorkers.filter(w => {
      const created = w.created;
      // Only include workers with valid created timestamp that existed before current period
      return created && typeof created === 'number' && created <= previousEnd;
    }).length;

    const previousPeriodActive = allWorkers.filter(w => {
      const created = w.created;
      // Only include workers with valid created timestamp that existed before current period and are currently active
      return created && typeof created === 'number' && created <= previousEnd && w.active && !w.deActivated;
    }).length;

    return {
      current: currentCreated,
      previous: previousCreated,
      previousPeriodTotal,
      previousPeriodActive
    };
  }, [washers, dateRangeState.startTimestamp, dateRangeState.endTimestamp]);

  // Calculate trend data for providers and workers based on total counts
  const providersTrendData = useMemo(() => {
    if (!providersStats.total && !providersPeriodStats.previousPeriodTotal) return null;

    const calculateTrend = (current, previous) => {
      if (current === null || current === undefined) return null;
      if (!previous || previous === 0) {
        return current > 0 ? 100 : null;
      }
      return ((current - previous) / previous) * 100;
    };

    // Calculate trend based on total counts
    const trendPercentage = calculateTrend(providersStats.total, providersPeriodStats.previousPeriodTotal);

    return {
      percentage: trendPercentage,
      currentTotal: providersStats.total,
      previousTotal: providersPeriodStats.previousPeriodTotal || 0,
      currentPeriod: currentPeriodRange,
      previousPeriod: previousPeriodRange
    };
  }, [providersStats, providersPeriodStats, currentPeriodRange, previousPeriodRange]);

  const workersTrendData = useMemo(() => {
    if (!workersStats.total && !workersPeriodStats.previousPeriodTotal) return null;

    const calculateTrend = (current, previous) => {
      if (current === null || current === undefined) return null;
      if (!previous || previous === 0) {
        return current > 0 ? 100 : null;
      }
      return ((current - previous) / previous) * 100;
    };

    // Calculate trend based on total counts
    const trendPercentage = calculateTrend(workersStats.total, workersPeriodStats.previousPeriodTotal);

    return {
      percentage: trendPercentage,
      currentTotal: workersStats.total,
      previousTotal: workersPeriodStats.previousPeriodTotal || 0,
      currentPeriod: currentPeriodRange,
      previousPeriod: previousPeriodRange
    };
  }, [workersStats, workersPeriodStats, currentPeriodRange, previousPeriodRange]);

  // Calculate totals from dailyAnalytics for the selected date range with trends
  const totals = useMemo(() => {
    if (!dailyAnalytics || dailyAnalytics.length === 0) {
      return [];
    }

    const role = user?.accessType || 'superAdmin';
    const isSPUser = !accessTypes.includes(role);
    const spId = isSPUser ? user?.uid : null;

    // Aggregate data from all days in the selected range (current period)
    let aggregatedData = {
      totalUsers: 0,
      totalRevenu: 0,
      totalCommission: 0,
      totalBookings: 0,
      totalRate: { count: 0, rate: 0 },
      counters: {
        onlineBooking: 0,
        offlineBooking: 0,
        canceled: 0,
        rejected: 0
      }
    };

    // Aggregate data from previous period
    let previousAggregatedData = {
      totalUsers: 0,
      totalRevenu: 0,
      totalCommission: 0,
      totalBookings: 0,
      totalRate: { count: 0, rate: 0 },
      counters: {
        onlineBooking: 0,
        offlineBooking: 0,
        canceled: 0,
        rejected: 0
      }
    };

    // Calculate current period totals
    dailyAnalytics.forEach(day => {
      if (isSPUser && spId) {
        // SP user - aggregate from SP data
        if (day.SP && day.SP[spId]) {
          const spData = day.SP[spId];
          aggregatedData.totalBookings += spData.totalBookings || 0;
          aggregatedData.totalRevenu += spData.totalRevenu || 0;
          aggregatedData.totalUsers += spData.totalUsers || 0;

          if (spData.totalRate) {
            aggregatedData.totalRate.count += spData.totalRate.count || 0;
            aggregatedData.totalRate.rate += spData.totalRate.rate || 0;
          }

          if (spData.counters) {
            aggregatedData.counters.onlineBooking += spData.counters.onlineBooking || 0;
            aggregatedData.counters.offlineBooking += spData.counters.offlineBooking || 0;
            aggregatedData.counters.canceled += spData.counters.canceled || 0;
            aggregatedData.counters.rejected += spData.counters.rejected || 0;
          }
        }
      } else {
        // Admin user - aggregate from general data
        if (day.general) {
          aggregatedData.totalBookings += day.general.totalBookings || 0;
          aggregatedData.totalRevenu += day.general.totalRevenu || 0;
          aggregatedData.totalCommission += day.general.totalCommission || 0;
          aggregatedData.totalUsers += day.general.totalUsers || 0;

          if (day.general.counters) {
            aggregatedData.counters.onlineBooking += day.general.counters.onlineBooking || 0;
            aggregatedData.counters.offlineBooking += day.general.counters.offlineBooking || 0;
            aggregatedData.counters.canceled += day.general.counters.canceled || 0;
            aggregatedData.counters.rejected += day.general.counters.rejected || 0;
          }
        }
      }
    });

    // Calculate previous period totals
    if (previousDailyAnalytics && previousDailyAnalytics.length > 0) {
      previousDailyAnalytics.forEach(day => {
        if (isSPUser && spId) {
          if (day.SP && day.SP[spId]) {
            const spData = day.SP[spId];
            previousAggregatedData.totalBookings += spData.totalBookings || 0;
            previousAggregatedData.totalRevenu += spData.totalRevenu || 0;
            previousAggregatedData.totalUsers += spData.totalUsers || 0;

            if (spData.totalRate) {
              previousAggregatedData.totalRate.count += spData.totalRate.count || 0;
              previousAggregatedData.totalRate.rate += spData.totalRate.rate || 0;
            }

            if (spData.counters) {
              previousAggregatedData.counters.onlineBooking += spData.counters.onlineBooking || 0;
              previousAggregatedData.counters.offlineBooking += spData.counters.offlineBooking || 0;
              previousAggregatedData.counters.canceled += spData.counters.canceled || 0;
              previousAggregatedData.counters.rejected += spData.counters.rejected || 0;
            }
          }
        } else {
          if (day.general) {
            previousAggregatedData.totalBookings += day.general.totalBookings || 0;
            previousAggregatedData.totalRevenu += day.general.totalRevenu || 0;
            previousAggregatedData.totalCommission += day.general.totalCommission || 0;
            previousAggregatedData.totalUsers += day.general.totalUsers || 0;

            if (day.general.counters) {
              previousAggregatedData.counters.onlineBooking += day.general.counters.onlineBooking || 0;
              previousAggregatedData.counters.offlineBooking += day.general.counters.offlineBooking || 0;
              previousAggregatedData.counters.canceled += day.general.counters.canceled || 0;
              previousAggregatedData.counters.rejected += day.general.counters.rejected || 0;
            }
          }
        }
      });
    }

    // Helper function to calculate trend data object
    const calculateTrendData = (current, previous) => {
      if (current === null || current === undefined) {
        return null;
      }

      const percentage = (!previous || previous === 0)
        ? (current > 0 ? 100 : null)
        : ((current - previous) / previous) * 100;

      return {
        percentage,
        currentTotal: current,
        previousTotal: previous || 0,
        currentPeriod: currentPeriodRange,
        previousPeriod: previousPeriodRange
      };
    };

    // Calculate average rating
    const avgRating = aggregatedData.totalRate.count > 0
      ? (aggregatedData.totalRate.rate / aggregatedData.totalRate.count).toFixed(2)
      : 0;

    const previousAvgRating = previousAggregatedData.totalRate.count > 0
      ? (previousAggregatedData.totalRate.rate / previousAggregatedData.totalRate.count).toFixed(2)
      : 0;

    // Featured SP performance (GA4-sourced). Admin aggregates across all SPs;
    // SP user sees their own row. Aggregation keys off the same user.uid the
    // rest of the dashboard uses for SP identity.
    const featuredCurrent = aggregateFeaturedTotals(featuredDaily, spId);
    const featuredPrevious = aggregateFeaturedTotals(previousFeaturedDaily, spId);

    // Per-placement (af_from) breakdown surfaced in the Featured Impressions /
    // Clicks / CTR stat-card hover tooltips.
    const featuredCurrentByPlacement = aggregateFeaturedByPlacement(featuredDaily, spId);
    const featuredPreviousByPlacement = aggregateFeaturedByPlacement(previousFeaturedDaily, spId);
    const placementsList = listPlacements(featuredCurrentByPlacement, featuredPreviousByPlacement);
    const buildPlacementItems = (source, field) =>
      placementsList.map((p) => ({
        label: formatPlacementLabel(p),
        value: source[p]?.[field] || 0,
        color: PLACEMENT_COLORS[p] || 'hsl(220, 9%, 60%)',
      }));
    const buildPlacementCtrItems = (source) =>
      placementsList.map((p) => ({
        label: formatPlacementLabel(p),
        value: source[p]?.ctr || 0,
        color: PLACEMENT_COLORS[p] || 'hsl(220, 9%, 60%)',
        formatAsPercent: true,
      }));

    const featuredCards = [
      {
        title: 'Featured Impressions',
        icon: Eye,
        value: formatNumber(featuredCurrent.impressions) || 0,
        color: '#8b5cf6',
        trendData: calculateTrendData(featuredCurrent.impressions, featuredPrevious.impressions),
        breakdownData: {
          currentItems: buildPlacementItems(featuredCurrentByPlacement, 'impressions'),
          previousItems: buildPlacementItems(featuredPreviousByPlacement, 'impressions'),
        },
      },
      {
        title: 'Featured Clicks',
        icon: MousePointerClick,
        value: formatNumber(featuredCurrent.clicks) || 0,
        color: '#ec4899',
        trendData: calculateTrendData(featuredCurrent.clicks, featuredPrevious.clicks),
        breakdownData: {
          currentItems: buildPlacementItems(featuredCurrentByPlacement, 'clicks'),
          previousItems: buildPlacementItems(featuredPreviousByPlacement, 'clicks'),
        },
      },
      {
        title: 'Featured CTR',
        icon: Target,
        value: `${featuredCurrent.ctr.toFixed(2)}%`,
        color: '#f59e0b',
        trendData: calculateTrendData(featuredCurrent.ctr, featuredPrevious.ctr),
        breakdownData: {
          currentItems: buildPlacementCtrItems(featuredCurrentByPlacement),
          previousItems: buildPlacementCtrItems(featuredPreviousByPlacement),
        },
      },
      {
        title: 'Featured Bounce Rate',
        icon: LogOut,
        value: `${(featuredCurrent.clicks > 0 ? ((featuredCurrent.clicks - featuredCurrent.orders) / featuredCurrent.clicks) * 100 : 0).toFixed(2)}%`,
        color: '#ef4444',
        invertTrend: true,
        trendData: calculateTrendData(
          featuredCurrent.clicks > 0 ? ((featuredCurrent.clicks - featuredCurrent.orders) / featuredCurrent.clicks) * 100 : 0,
          featuredPrevious.clicks > 0 ? ((featuredPrevious.clicks - featuredPrevious.orders) / featuredPrevious.clicks) * 100 : 0,
        ),
      },
      {
        title: 'Featured Revenue',
        icon: Trophy,
        value: formatNumber(featuredCurrent.revenue) || 0,
        color: '#14b8a6',
        formatCurrency: true,
        trendData: calculateTrendData(featuredCurrent.revenue, featuredPrevious.revenue),
      },
    ];

    // Build totals array based on user role
    if (isSPUser) {
      // Count washers for this SP
      // const spWashersCount = Object.values(washers || {}).filter(w => w?.sp_id === user?.uid).length;

      return [
        // {
        //   title: 'Total Washers',
        //   icon: "iconsminds-engineering",
        //   value: spWashersCount || 0,
        //   color: "#00BCD4",
        //   trendData: null // Static count, no trend
        // },

        {
          title: 'Workers',
          icon: UserCheck,
          value: `${formatNumber(workersStats.total)} / ${formatNumber(workersStats.active)}`,
          color: "#10b981",
          trendData: null,
          trendLabel: 'All workers vs active workers',
          activePercentage: workersStats.total > 0
            ? ((workersStats.active / workersStats.total) * 100).toFixed(1)
            : 0,
          breakdownData: {
            currentTotal: workersStats.total,
            currentActive: workersStats.active,
            previousTotal: workersPeriodStats.previousPeriodTotal,
            previousActive: workersPeriodStats.previousPeriodActive
          }
        },
        {
          title: 'Total Users',
          icon: Users,
          value: formatNumber(aggregatedData.totalUsers) || 0,
          color: "#3F51B5",
          trendData: calculateTrendData(aggregatedData.totalUsers, previousAggregatedData.totalUsers)
        },
        {
          title: 'Total Sales',
          icon: DollarSign,
          value: formatNumber(aggregatedData.totalRevenu) || 0,
          color: "#278664",
          formatCurrency: true,
          trendData: calculateTrendData(aggregatedData.totalRevenu, previousAggregatedData.totalRevenu)
        },
        {
          title: 'Average Rating',
          icon: Star,
          value: avgRating || 0,
          color: "#FFE234",
          trendData: calculateTrendData(parseFloat(avgRating), parseFloat(previousAvgRating))
        },
        {
          title: 'Total Bookings',
          icon: ShoppingCart,
          value: formatNumber(aggregatedData.totalBookings) || 0,
          color: "#9C27B0",
          trendData: calculateTrendData(aggregatedData.totalBookings, previousAggregatedData.totalBookings),
          breakdownData: {
            currentOnline: aggregatedData.counters.onlineBooking,
            currentOffline: aggregatedData.counters.offlineBooking,
            previousOnline: previousAggregatedData.counters.onlineBooking,
            previousOffline: previousAggregatedData.counters.offlineBooking
          }
        },
        {
          title: 'Total Canceled',
          icon: XCircle,
          value: formatNumber(aggregatedData.counters.canceled) || 0,
          color: "#dc2626",
          trendData: calculateTrendData(
            aggregatedData.counters.canceled,
            previousAggregatedData.counters.canceled
          ),
          invertTrend: true, // Increase is bad
        },
        {
          title: 'Total Rejected',
          icon: Ban,
          value: formatNumber(aggregatedData.counters.rejected) || 0,
          color: "#ef4444",
          trendData: calculateTrendData(
            aggregatedData.counters.rejected,
            previousAggregatedData.counters.rejected
          ),
          invertTrend: true, // Increase is bad
        },
        ...(showFeatured ? featuredCards : []),

      ];
    } else {
      // Admin user
      return [
        // {
        //   title: 'Total Service Providers',
        //   icon: "iconsminds-shop-4",
        //   value: (providers && Object.keys(providers).length) || 0,
        //   trendData: null // Static count, no trend
        // },
        // {
        //   title: 'Total Washers',
        //   icon: "iconsminds-engineering",
        //   value: (washers && Object.keys(washers).length) || 0,
        //   color: "#00BCD4",
        //   trendData: null // Static count, no trend
        // },
        {
          title: 'Service Providers',
          icon: Building2,
          value: `${formatNumber(providersStats.total)} / ${formatNumber(providersStats.active)}`,
          color: '#3b82f6',
          trendData: providersTrendData,
          trendLabel: 'All providers vs active providers',
          breakdownData: {
            currentTotal: providersStats.total,
            currentActive: providersStats.active,
            previousTotal: providersPeriodStats.previousPeriodTotal,
            previousActive: providersPeriodStats.previousPeriodActive
          }
        },
        {
          title: 'Workers',
          icon: UserCheck,
          value: `${formatNumber(workersStats.total)} / ${formatNumber(workersStats.active)}`,
          color: '#10b981',
          trendData: workersTrendData,
          trendLabel: 'All workers vs active workers',
          breakdownData: {
            currentTotal: workersStats.total,
            currentActive: workersStats.active,
            previousTotal: workersPeriodStats.previousPeriodTotal,
            previousActive: workersPeriodStats.previousPeriodActive
          }
        },
        {
          title: 'Total Users',
          icon: Users,
          value: formatNumber(aggregatedData.totalUsers) || 0,
          color: '#3F51B5',
          trendData: calculateTrendData(aggregatedData.totalUsers, previousAggregatedData.totalUsers)
        },
        {
          title: 'Total Sales',
          icon: DollarSign,
          value: formatNumber(aggregatedData.totalRevenu) || 0,
          color: '#278664',
          formatCurrency: true,
          trendData: calculateTrendData(aggregatedData.totalRevenu, previousAggregatedData.totalRevenu)
        },
        {
          title: 'Chill Revenue',
          icon: DollarSign,
          value: formatNumber(aggregatedData.totalCommission) || 0,
          color: "#FF9F40",
          formatCurrency: true,
          trendData: calculateTrendData(aggregatedData.totalCommission, previousAggregatedData.totalCommission)
        },
        {
          title: 'Total Bookings',
          icon: ShoppingCart,
          value: formatNumber(aggregatedData.totalBookings) || 0,
          color: "#9C27B0",
          trendData: calculateTrendData(aggregatedData.totalBookings, previousAggregatedData.totalBookings),
          breakdownData: {
            currentOnline: aggregatedData.counters.onlineBooking,
            currentOffline: aggregatedData.counters.offlineBooking,
            previousOnline: previousAggregatedData.counters.onlineBooking,
            previousOffline: previousAggregatedData.counters.offlineBooking
          }
        },
        {
          title: 'Total Canceled',
          icon: XCircle,
          value: formatNumber(aggregatedData.counters.canceled) || 0,
          color: '#dc2626',
          trendData: calculateTrendData(
            aggregatedData.counters.canceled,
            previousAggregatedData.counters.canceled
          ),
          invertTrend: true, // Increase is bad
        },
        {
          title: 'Total Rejected',
          icon: Ban,
          value: formatNumber(aggregatedData.counters.rejected) || 0,
          color: '#ef4444',
          trendData: calculateTrendData(
            aggregatedData.counters.rejected,
            previousAggregatedData.counters.rejected
          ),
          invertTrend: true, // Increase is bad
        },
        ...(showFeatured ? featuredCards : []),

      ];
    }
  }, [dailyAnalytics, previousDailyAnalytics, featuredDaily, previousFeaturedDaily, showFeatured, user?.uid, user?.accessType, currentPeriodRange, previousPeriodRange, providersStats, workersStats, providersTrendData, workersTrendData, providersPeriodStats.previousPeriodTotal, providersPeriodStats.previousPeriodActive, workersPeriodStats.previousPeriodTotal, workersPeriodStats.previousPeriodActive]);

  // Fetch recent bookings for SP users
  useEffect(() => {
    if (!user?.uid) return;

    const role = user?.accessType || 'superAdmin';
    if (!accessTypes.includes(role)) {
      // SP user - fetch recent bookings
      firestore().collection(ORDER_COLLECTION)
        .where('sp_id', '==', user?.uid)
        .where('created', '>=', dateRangeState.startTimestamp)
        .where('created', '<=', dateRangeState.endTimestamp)
        .orderBy('created', 'desc')
        .limit(6)
        .get()
        .then((snapshot) => {
          const newBookings = [];
          snapshot.docs.forEach(doc => {
            newBookings.push(doc.data());
          });
          setBookings(newBookings);
        })
        .catch((error) => {
          console.error('Error fetching bookings:', error);
          setBookings([]);
        });
    }
  }, [user?.uid, user?.accessType, dateRangeState.startTimestamp, dateRangeState.endTimestamp]);

  // Memoize selectProvider callback
  const selectProvider = useCallback((provider) => {
    setSelectedProvider(provider);
  }, []);

  // Memoize date range change handler
  const handleDateRangeChange = useCallback((newDateRange) => {
    setDateRangeState(newDateRange);
  }, []);

  // Memoize stat cards data
  const statCardsData = useMemo(() => {
    return totals.map((item, index) => {
      const isCurrency = item.formatCurrency ||
        item.title.toLowerCase().includes('sales') ||
        item.title.toLowerCase().includes('revenue') ||
        item.title.toLowerCase().includes('chill revenue');

      return {
        key: `stat_card_${index}`,
        item,
        isCurrency
      };
    });
  }, [totals]);

  if (selectedProvider) {
    return (
      <SelectedServiceProvider
        provider={selectedProvider}
        selectProvider={selectProvider}
      />
    );
  }

  return (
    analyticsLoading ? (
      <div className="loading" />
    ) : (
      <Fragment>
        <div className="mb-3">
          <Row>
            <Colxx xxs="12" md="6">
              <h1 className="mb-2 p-0">Dashboard</h1>
            </Colxx>
            <Colxx xxs="12" md="6" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', }}>
              <DateRangeSelector
                onRangeChange={handleDateRangeChange}
                defaultDays={7}
                value={dateRangeState}
              />
            </Colxx>
          </Row>
        </div>
        {/* <Separator className="mb-5" /> */}
        <Row className="mt-2">
          {statCardsData.map(({ key, item, isCurrency }) => (
            <Colxx key={key} xxs="12" sm="6" md="6" lg="3" className="mb-4">
              <StatCard
                title={item.title}
                value={item.value}
                icon={item.icon}
                color={item.color || "#666"}
                formatCurrency={isCurrency}
                trendData={item.trendData}
                invertTrend={item.invertTrend}
                breakdownData={item.breakdownData}
                trendLabel={item.trendLabel}
              />
            </Colxx>
          ))}
        </Row>
        <Row>
          {!isAdmin && (
            <Colxx xxs="12" md="6" className="mb-4">
              {bookings && <RecentOrders bookings={bookings} />}
            </Colxx>
          )}

          {isAdmin && (
            <Colxx xxs="12" md="6" className="mb-4">
              <TopProviders
                dateRange={dateRangeState}
                dailyAnalytics={dailyAnalytics}
                previousDailyAnalytics={previousDailyAnalytics}
                loading={analyticsLoading}
                error={analyticsError}
                onProviderClick={selectProvider}
              />
            </Colxx>
          )}
          <Colxx xxs="12" md="6" className="mb-4">
            <TopWashers
              dateRange={dateRangeState}
              dailyAnalytics={dailyAnalytics}
              previousDailyAnalytics={previousDailyAnalytics}
              loading={analyticsLoading}
              error={analyticsError}
            />
          </Colxx>
          <DashboardCharts
            dateRange={dateRangeState}
            dailyAnalytics={dailyAnalytics}
            previousDailyAnalytics={previousDailyAnalytics}
            loading={analyticsLoading}
            error={analyticsError}
          />
          {showFeatured && (
            <FeaturedCharts
              dateRange={dateRangeState}
              featuredDaily={featuredDaily}
              previousFeaturedDaily={previousFeaturedDaily}
              loading={featuredLoading}
              error={featuredError}
            />
          )}
        </Row>
      </Fragment>
    )
  );
};



// Memoize DefaultDashboard component
export default React.memo(DefaultDashboard);
